import type { SvgIconComponent } from '@mui/icons-material';
import Article from '@mui/icons-material/Article';
import type { SvgIconProps, SxProps, Theme } from '@mui/material';
import { Box, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';

export type PageHeaderProps = {
  title: string;
  titleAddon?: React.ReactNode;
  titleGridStyle?: SxProps<Theme>;
  icon?: SvgIconComponent | React.FC<SvgIconProps>;
  titleRight?: string | null;
};

/**
 * Height of the title row, reserved whether or not the page has an addon. Portfolio and
 * Spending hang a view switcher off the title, which is taller than the title itself,
 * so without a floor the band — and everything under it — jumped by ~13px between
 * pages. Sized to the tallest thing that goes in the row.
 */
const TITLE_ROW_HEIGHT = 40;

/**
 * The page's title band. It used to carry the table search too, which now sits on the
 * grid toolbar beside Export; the global filter band it could also render was already
 * dead, since those live in `AppBar`.
 */
export const PageHeader = ({
  title,
  titleAddon,
  icon: Icon = Article,
  titleGridStyle,
  titleRight,
}: PageHeaderProps) => {
  return (
    <Grid bgcolor="#F9FAFB" container pb={0.5} justifyContent="space-between" sx={titleGridStyle}>
      <Grid
        size={{ xs: 12, sm: 6, md: 6 }}
        p={0}
        container
        alignItems="center"
        sx={{
          mb: { xs: 1, sm: 0 },
          // Addons arrive at their own natural height; a view switcher is a fraction taller than
          // the row it sits in, which would push the title off the shared baseline.
          '& .MuiToggleButtonGroup-root': { height: TITLE_ROW_HEIGHT },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', minHeight: TITLE_ROW_HEIGHT }}>
          <Box pr={1} sx={{ display: 'flex' }}>
            <Icon color="primary" fontSize="medium" />
          </Box>
          <Typography variant="h6">{title}</Typography>
        </Box>
        {titleAddon}
      </Grid>
      {titleRight ? (
        <Grid
          size={{ xs: 12, sm: 6, md: 6 }}
          justifyContent={{ xs: 'flex-start', sm: 'flex-end' }}
          display="flex"
          alignItems="center"
          sx={{ color: 'text.secondary', fontSize: '14px' }}
        >
          {titleRight}
        </Grid>
      ) : null}
    </Grid>
  );
};
